import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "../TimeTracker/TimeTracker.css";
import {
    getActivityDayView,
    getActivitySummary,
    getActivityNotes,
    saveActivityNote,
} from "../../services/timeTracker.service";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    Box,
    Grid2,
    LinearProgress,
    Stack,
    Typography,
    Divider,
    Dialog,
    DialogContent,
    IconButton,
    DialogTitle,
    Button,
    CircularProgress,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Checkbox,
    Chip,
} from "@mui/material";
import { ToastSuccess, ToastError } from "../../utils/Toaster/Toaster";
import NoImage from "../../assets/images/No_image.jpg";
import CloseIcon from "@mui/icons-material/Close";
import { encryptData } from "../../utils/cryptoUtils";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import MouseIcon from "@mui/icons-material/Mouse";
import MonitorIcon from "@mui/icons-material/Monitor";
import LanguageIcon from "@mui/icons-material/Language";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import LiveScreenModal from "./LiveScreenModal";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const NOTE_REASONS = [
    { value: "meeting", label: "Meeting" },
    { value: "reading", label: "Reading" },
    { value: "planning", label: "Planning" },
    { value: "call", label: "Call" },
    { value: "break", label: "Break" },
    { value: "other", label: "Other" },
];

// A note only explains an untracked slot; a manager/admin has to approve it
// before those minutes count as worked time (Daily Reports → Note Approvals).
const NOTE_STATUS_STYLES = {
    pending: { label: "Pending approval", color: "#b45309", border: "#f59e0b" },
    approved: { label: "Approved", color: "#15803d", border: "rgb(5, 169, 38)" },
    rejected: { label: "Rejected", color: "#b91c1c", border: "#f87171" },
};

// Notes written before the approval flow existed carry no status — they are
// undecided, so they read as pending.
const noteStatusOf = (note) => (note ? note.status || "pending" : null);

// A slot is eligible for the multi-select notes checkbox if:
// 1. It is not a pure "On Break" slot (pure break has no screenshots/activity data and is marked on break)
// 2. It meets any of the following:
//    a. It is "No Activity" (no screenshot / no tracked data)
//    b. It has tracked duration below 10 minutes (< 10 min, e.g. "17% of 8 min", "48% of 9 min")
//    c. It has low activity percentage (<= 10%, e.g. "3% of 10 min")
const isSlotEligibleForNote = (slot) => {
    if (!slot) return false;

    const hasScreenshot = Boolean(
        slot.screenshots && slot.screenshots.length > 0 &&
        (slot.screenshots[0]?.data || slot.screenshots[0]?._id || slot.screenshots[0])
    );
    const hasData = Boolean(slot.hasData || hasScreenshot);

    // Pure break slot: no screenshots and no activity data, and on break.
    // Displayed as "On Break" (coffee cup icon). Not eligible for activity notes.
    if (!hasData && slot.onBreak) {
        return false;
    }

    // No activity slot: no screenshots and no activity data, not on break.
    // Displayed as "No Activity". Eligible for notes to account for missing time.
    if (!hasData) {
        return true;
    }

    // Active slot (has screenshots or activity data):
    // Calculate tracked duration (matches what is displayed on the slot card)
    const workMinutes = slot.workSec != null ? Math.round(Number(slot.workSec) / 60) : 0;
    const sampleCount = slot.sampleCount != null ? Number(slot.sampleCount) : 0;
    const displayMinutes = Math.max(workMinutes, sampleCount);

    const trackedMins = slot.trackedMinutes != null
        ? Number(slot.trackedMinutes)
        : displayMinutes;

    const pct = slot.activityPct != null ? Number(slot.activityPct) : 0;

    // Eligible if:
    // - Low activity (<= 10%)
    // - OR tracked time is below 10 minutes (< 10 min, e.g. 8 min, 9 min)
    return pct <= 10 || trackedMins < 10 || displayMinutes < 10;
};

function fmtTime(hour, minute) {
    const ampm = hour >= 12 ? "PM" : "AM";
    const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${display}:${String(minute).padStart(2, "0")} ${ampm}`;
}

function fmtDuration(min) {
    if (!min) return "0 min";
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m} min`;
    return `${h}h ${m}m`;
}

function getProgressColor(pct) {
    if (pct >= 60) return "#4caf78";
    if (pct >= 30) return "#e0a23a";
    return "#e64a4a";
}

const UserDetailView = ({
    userId,
    userName,
    isAdmin,
    onBack,
    selectedDate: propSelectedDate,
    setSelectedDate: propSetSelectedDate,
}) => {
    const [day, setDay] = useState(null);
    // Toggles the "from when to when" break breakdown under the Break Time card.
    const [showBreaks, setShowBreaks] = useState(false);
    const [appSummary, setAppSummary] = useState(null);
    const [notes, setNotes] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAppActivity, setShowAppActivity] = useState(false);
    const [carouselShots, setCarouselShots] = useState(null); // { shots, index }
    const [localSelectedDate, setLocalSelectedDate] = useState(dayjs());
    const [showLive, setShowLive] = useState(false); // live screen modal
    const [selectedSlots, setSelectedSlots] = useState([]); // array of slotKey strings, e.g. ["7:0", "7:1"]
    const [slotDrafts, setSlotDrafts] = useState({}); // { [slotKey]: { reason, text } }
    const [commonReason, setCommonReason] = useState("meeting");
    const [commonText, setCommonText] = useState("");
    const [commonSaving, setCommonSaving] = useState(false);
    const navigate = useNavigate();

    const selectedDate = propSelectedDate || localSelectedDate;
    const setSelectedDate = propSetSelectedDate || setLocalSelectedDate;

    const isOwnActivity = !isAdmin; // admin viewing someone else; user viewing themselves
    const editable = isOwnActivity || isAdmin;

    const loadDay = async (date) => {
        if (!userId) return;
        setLoading(true);
        setError(null);
        try {
            const formattedDate = dayjs(date).format("YYYY-MM-DD");
            const encDate = encryptData(formattedDate);
            const encUserId = encryptData(userId);

            const [dayRes, notesRes] = await Promise.all([
                getActivityDayView(encDate, encUserId),
                getActivityNotes(encDate, encUserId).catch(() => ({ data: { notes: [] } })),
            ]);

            // Response interceptor unwraps axios → `res` is already the backend payload `{ error, data, ... }`.
            const rawDay = dayRes?.data ? { ...dayRes.data } : null;
            if (rawDay && Array.isArray(rawDay.hours) && rawDay.hours.length > 0) {
                const hourMap = new Map();
                for (const h of rawDay.hours) {
                    hourMap.set(h.hour, h);
                }

                const minHour = Math.min(...rawDay.hours.map((h) => h.hour));
                let maxHour = Math.max(...rawDay.hours.map((h) => h.hour));

                // If viewing today, ensure hours up to current hour are included
                if (dayjs(date).isSame(dayjs(), "day")) {
                    maxHour = Math.max(maxHour, dayjs().hour());
                }

                // Check breaks to mark empty slots that fall in break periods
                const breakSlotSet = new Set();
                if (Array.isArray(rawDay.breaks)) {
                    for (const b of rawDay.breaks) {
                        if (!b.startedAt) continue;
                        const start = dayjs(b.startedAt);
                        const end = b.endedAt ? dayjs(b.endedAt) : dayjs();
                        for (let t = start; t.isBefore(end) || t.isSame(end); t = t.add(1, "minute")) {
                            if (t.format("YYYY-MM-DD") === formattedDate) {
                                breakSlotSet.add(`${t.hour()}:${Math.floor(t.minute() / 10)}`);
                            }
                        }
                    }
                }

                const filledHours = [];
                for (let h = minHour; h <= maxHour && h < 24; h++) {
                    if (hourMap.has(h)) {
                        filledHours.push(hourMap.get(h));
                    } else {
                        filledHours.push({
                            hour: h,
                            workSec: 0,
                            workMinutes: 0,
                            slots: Array.from({ length: 6 }, (_, i) => ({
                                slotIdx: i,
                                startMinute: i * 10,
                                endMinute: (i + 1) * 10,
                                activityPct: 0,
                                workSec: 0,
                                totalKeys: 0,
                                totalMouse: 0,
                                keyboardActiveSeconds: 0,
                                mouseActiveSeconds: 0,
                                sampleCount: 0,
                                trackedMinutes: 0,
                                approvedNoteMinutes: 0,
                                noteStatus: null,
                                screenshots: [],
                                screenshot: null,
                                hasData: false,
                                onBreak: breakSlotSet.has(`${h}:${i}`),
                                suspiciousScore: 0,
                                suspiciousReasons: [],
                                suspiciousAppName: "",
                                apps: [],
                            })),
                        });
                    }
                }
                rawDay.hours = filledHours.sort((a, b) => a.hour - b.hour);
            }
            setDay(rawDay);

            const notesList = notesRes?.data?.notes || [];
            const noteMap = {};
            for (const n of notesList) noteMap[n.slotKey] = n;
            setNotes(noteMap);
        } catch (err) {
            console.error("Error loading day:", err);
            setError("Error fetching activity");
        } finally {
            setLoading(false);
        }
    };

    // Fetch the App Activity summary lazily — only after the user clicks the toggle.
    // Skipping it on initial page load saves a backend round-trip when nobody opens the panel.
    const loadAppSummary = async (date) => {
        if (!userId) return;
        try {
            const formattedDate = dayjs(date).format("YYYY-MM-DD");
            const summaryRes = await getActivitySummary(
                encryptData(`${formattedDate}T00:00:00Z`),
                encryptData(`${formattedDate}T23:59:59Z`),
                encryptData(userId)
            );
            setAppSummary(summaryRes?.data || null);
        } catch (err) {
            console.error("Error loading app summary:", err);
        }
    };

    useEffect(() => {
        // Switching user/date invalidates a stale summary so it re-fetches when next opened.
        setAppSummary(null);
        setSelectedSlots([]);
        setSlotDrafts({});
        setCommonText("");
        setCommonReason("meeting");
        loadDay(selectedDate);
    }, [selectedDate, userId]);

    useEffect(() => {
        if (showAppActivity && !appSummary) loadAppSummary(selectedDate);
    }, [showAppActivity, selectedDate, userId]);

    const handleUpdateSlotDraft = (slotKey, field, value) => {
        setSlotDrafts((prev) => {
            const current = prev[slotKey] || {
                reason: notes[slotKey]?.reason || "meeting",
                text: notes[slotKey]?.text || "",
            };
            return {
                ...prev,
                [slotKey]: {
                    ...current,
                    [field]: value,
                },
            };
        });
    };

    const handleToggleSlot = (slotKey) => {
        setSelectedSlots((prev) => {
            const exists = prev.includes(slotKey);
            if (exists) {
                return prev.filter((k) => k !== slotKey);
            } else {
                setSlotDrafts((d) => {
                    if (!d[slotKey]) {
                        return {
                            ...d,
                            [slotKey]: {
                                reason: notes[slotKey]?.reason || commonReason || "meeting",
                                text: notes[slotKey]?.text !== undefined ? notes[slotKey].text : commonText || "",
                            },
                        };
                    }
                    return d;
                });
                return [...prev, slotKey];
            }
        });
    };

    const handleAddNoteClick = (slotKey) => {
        setSlotDrafts((d) => {
            if (!d[slotKey]) {
                return {
                    ...d,
                    [slotKey]: {
                        reason: notes[slotKey]?.reason || commonReason || "meeting",
                        text: notes[slotKey]?.text !== undefined ? notes[slotKey].text : commonText || "",
                    },
                };
            }
            return d;
        });
        setSelectedSlots((prev) => (prev.includes(slotKey) ? prev : [...prev, slotKey]));
    };

    const handleEditNoteClick = (slotKey) => {
        setSlotDrafts((d) => ({
            ...d,
            [slotKey]: {
                reason: notes[slotKey]?.reason || "meeting",
                text: notes[slotKey]?.text || "",
            },
        }));
        setSelectedSlots((prev) => (prev.includes(slotKey) ? prev : [...prev, slotKey]));
    };

    const handleToggleHour = (hourNumber) => {
        const hourObj = day?.hours?.find((h) => h.hour === hourNumber);
        if (!hourObj) return;
        const hourSlotKeys = (hourObj.slots || [])
            .filter(isSlotEligibleForNote)
            .map((s) => `${hourNumber}:${s.slotIdx}`);
        if (hourSlotKeys.length === 0) return;
        const allSelected = hourSlotKeys.every((k) => selectedSlots.includes(k));

        if (allSelected) {
            setSelectedSlots((prev) => prev.filter((k) => !hourSlotKeys.includes(k)));
        } else {
            setSlotDrafts((d) => {
                const next = { ...d };
                for (const k of hourSlotKeys) {
                    if (!next[k]) {
                        next[k] = {
                            reason: notes[k]?.reason || commonReason || "meeting",
                            text: notes[k]?.text !== undefined ? notes[k].text : commonText || "",
                        };
                    }
                }
                return next;
            });
            setSelectedSlots((prev) => Array.from(new Set([...prev, ...hourSlotKeys])));
        }
    };

    const handleSelectAllDay = () => {
        if (!day?.hours) return;
        const allKeys = [];
        for (const h of day.hours) {
            for (const s of (h.slots || [])) {
                if (isSlotEligibleForNote(s)) {
                    allKeys.push(`${h.hour}:${s.slotIdx}`);
                }
            }
        }
        setSlotDrafts((d) => {
            const next = { ...d };
            for (const k of allKeys) {
                if (!next[k]) {
                    next[k] = {
                        reason: notes[k]?.reason || commonReason || "meeting",
                        text: notes[k]?.text !== undefined ? notes[k].text : commonText || "",
                    };
                }
            }
            return next;
        });
        setSelectedSlots(allKeys);
    };

    const handleClearSelection = () => {
        setSelectedSlots([]);
        setSlotDrafts({});
    };

    const handleApplyCommonToAll = () => {
        if (selectedSlots.length === 0) return;
        setSlotDrafts((prev) => {
            const next = { ...prev };
            for (const k of selectedSlots) {
                next[k] = {
                    reason: commonReason,
                    text: commonText,
                };
            }
            return next;
        });
        ToastSuccess(`Applied note to ${selectedSlots.length} selected slot(s)`);
    };

    const handleSaveCommonNotes = async () => {
        if (selectedSlots.length === 0) return;
        setCommonSaving(true);
        try {
            const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
            const notesToSave = selectedSlots.map((slotKey) => {
                const draft = slotDrafts[slotKey];
                return {
                    slotKey,
                    reason: draft?.reason || notes[slotKey]?.reason || commonReason || "meeting",
                    text: (draft?.text !== undefined ? draft.text : notes[slotKey]?.text || commonText || "").trim(),
                };
            });

            const payload = {
                date: encryptData(formattedDate),
                userId: encryptData(userId),
                notes: notesToSave,
            };
            const res = await saveActivityNote(payload);
            const savedNotes = res?.data?.notes || (res?.data?.note ? [res?.data?.note] : []);
            if (savedNotes.length > 0) {
                setNotes((prev) => {
                    const nextNotes = { ...prev };
                    for (const n of savedNotes) {
                        nextNotes[n.slotKey] = n;
                    }
                    return nextNotes;
                });
            }
            ToastSuccess(`Saved notes for ${selectedSlots.length} slot${selectedSlots.length > 1 ? "s" : ""}`);
            setSelectedSlots([]);
            setSlotDrafts({});
            setCommonText("");
            setCommonReason("meeting");
            await loadDay(selectedDate);
        } catch (err) {
            console.error("Error saving notes:", err);
            ToastError("Failed to save notes");
        } finally {
            setCommonSaving(false);
        }
    };

    const handleSaveSingleSlot = async (slotKey) => {
        setCommonSaving(true);
        try {
            const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
            const draft = slotDrafts[slotKey];
            const payload = {
                date: encryptData(formattedDate),
                userId: encryptData(userId),
                slotKey,
                reason: draft?.reason || notes[slotKey]?.reason || "meeting",
                text: (draft?.text !== undefined ? draft.text : notes[slotKey]?.text || "").trim(),
            };
            const res = await saveActivityNote(payload);
            const note = res?.data?.note;
            if (note) setNotes((prev) => ({ ...prev, [slotKey]: note }));
            ToastSuccess("Note saved successfully");
            setSelectedSlots((prev) => prev.filter((k) => k !== slotKey));
            setSlotDrafts((prev) => {
                const next = { ...prev };
                delete next[slotKey];
                return next;
            });
            await loadDay(selectedDate);
        } catch (err) {
            console.error("Error saving note:", err);
            ToastError("Failed to save note");
        } finally {
            setCommonSaving(false);
        }
    };

    const formatSlotRange = (slotKey) => {
        const [hStr, sStr] = slotKey.split(":");
        const h = parseInt(hStr, 10);
        const s = parseInt(sStr, 10);
        const start = fmtTime(h, s * 10);
        const end = fmtTime(s === 5 ? (h + 1) % 24 : h, s === 5 ? 0 : (s + 1) * 10);
        return `${start} - ${end}`;
    };

    const sortedSelectedSlots = useMemo(() => {
        return [...selectedSlots].sort((a, b) => {
            const [hA, sA] = a.split(":").map(Number);
            const [hB, sB] = b.split(":").map(Number);
            if (hA !== hB) return hA - hB;
            return sA - sB;
        });
    }, [selectedSlots]);

    const slotMap = useMemo(() => {
        const map = new Map();
        if (day?.hours) {
            for (const h of day.hours) {
                for (const s of h.slots || []) {
                    map.set(`${h.hour}:${s.slotIdx}`, s);
                }
            }
        }
        return map;
    }, [day]);

    const totalSelectedNoteMinutes = useMemo(() => {
        return selectedSlots.reduce((sum, slotKey) => {
            const slot = slotMap.get(slotKey);
            const tracked = slot?.trackedMinutes !== undefined ? slot.trackedMinutes : (slot?.sampleCount || 0);
            const noteMins = Math.max(0, 10 - tracked);
            return sum + noteMins;
        }, 0);
    }, [selectedSlots, slotMap]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress size={30} thickness={4} sx={{ color: "#008080" }} />
            </Box>
        );
    }
    if (error) return <p>{error}</p>;

    return (
        <div
            className="app-container"
            style={{
                paddingBottom: editable && selectedSlots.length > 0 ? "140px" : undefined,
            }}
        >
            <Grid2 container mb={1} alignItems="center">
                <Grid2 size={{ xs: 6 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {isAdmin && onBack && (
                            <IconButton
                                onClick={onBack}
                                sx={{
                                    bgcolor: "#008080",
                                    color: "white",
                                    "&:hover": { bgcolor: "#006666" },
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        )}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={["DatePicker"]}>
                                <DatePicker
                                    value={selectedDate}
                                    className="datePicker w-100"
                                    format="DD/MM/YYYY"
                                    onChange={(d) => setSelectedDate(d)}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                    </Stack>
                </Grid2>
                <Grid2
                    size={{ xs: 6 }}
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    gap={2}
                >
                    {isAdmin && userName && (
                        <Typography variant="h6" fontWeight="bold">
                            {userName}
                        </Typography>
                    )}
                    {isAdmin && (
                        <Button
                            variant="contained"
                            size="small"
                            color="error"
                            startIcon={<LiveTvIcon fontSize="small" />}
                            onClick={() => setShowLive(true)}
                        >
                            Watch Live
                        </Button>
                    )}
                    {/* Opens the Productivity feature in its own page for this user + date. */}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LanguageIcon fontSize="small" />}
                        onClick={() => {
                            const d = dayjs(selectedDate).format("YYYY-MM-DD");
                            const q = new URLSearchParams({ userId: userId || "", name: userName || "", date: d });
                            navigate(`/home/productivity?${q.toString()}`);
                        }}
                        sx={{ borderColor: "#16a34a", color: "#16a34a" }}
                    >
                        Insights
                    </Button>
                </Grid2>
            </Grid2>

            {/* Summary cards */}
            {day && (
                <Box
                    sx={{
                        border: "1px solid lightgray",
                        padding: 2,
                        marginBottom: 2,
                        borderRadius: 2,
                        bgcolor: "#fff",
                        marginTop: 3,
                    }}
                >
                    <Grid2 container alignItems="center">
                        <Grid2 size={{ xs: 3.8 }}>
                            <Typography className="activity-time">Worked Time</Typography>
                            <Typography>{fmtDuration(day.summary?.totalWorkMinutes || 0)}</Typography>
                            {/* Minutes that came from approved notes rather than tracked
                                activity, so the figure is never a black box. */}
                            {day.summary?.pendingNoteMinutes > 0 && (
                                <Typography variant="caption" sx={{ color: "#b45309", display: "block" }}>
                                    +{day.summary.pendingNoteMinutes} min pending approval
                                </Typography>
                            )}
                            {day.summary?.approvedNoteMinutes > 0 && (
                                <Typography variant="caption" sx={{ color: "#15803d", display: "block" }}>
                                    incl. {day.summary.approvedNoteMinutes} min from approved notes
                                </Typography>
                            )}
                        </Grid2>
                        <Grid2 size={{ xs: 0.3 }} display="flex" alignItems="center" justifyContent="center">
                            <Divider orientation="vertical" style={{ height: "100%" }} />
                        </Grid2>
                        <Grid2 size={{ xs: 3.8 }}>
                            <Typography>Avg. Activity</Typography>
                            <Typography>{day.summary?.avgActivityPct || 0}%</Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 0.3 }} display="flex" alignItems="center" justifyContent="center">
                            <Divider orientation="vertical" style={{ height: "100%" }} />
                        </Grid2>
                        {/* Total break time — click to expand the from-when-to-when breakdown. */}
                        <Grid2
                            size={{ xs: 3.8 }}
                            onClick={() => (day.breaks?.length ? setShowBreaks((v) => !v) : null)}
                            sx={{ cursor: day.breaks?.length ? "pointer" : "default" }}
                        >
                            <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <LocalCafeIcon sx={{ fontSize: 18, color: "#d97706" }} />
                                Break Time{" "}
                                {day.breaks?.length ? (
                                    <span style={{ fontSize: 11, color: "#d97706" }}>
                                        {showBreaks ? "▴ hide" : "▾ details"}
                                    </span>
                                ) : null}
                            </Typography>
                            <Typography sx={{ color: "#b45309", fontWeight: 600 }}>
                                {fmtDuration(Math.round((day.summary?.totalBreakSec || 0) / 60))}
                            </Typography>
                        </Grid2>
                    </Grid2>

                    {/* "When to when" break breakdown */}
                    {showBreaks && day.breaks?.length > 0 && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed #e1e5eb" }}>
                            {day.breaks.map((b, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        py: 0.5,
                                        fontSize: 13,
                                    }}
                                >
                                    <span style={{ color: "#b45309", display: "flex", alignItems: "center", gap: 4 }}>
                                        <LocalCafeIcon sx={{ fontSize: 16, color: "#d97706" }} />
                                        Break {i + 1}
                                        {b.reason ? ` (${b.reason})` : ""}
                                    </span>
                                    <span style={{ color: "#64748b" }}>
                                        {dayjs(b.startedAt).format("hh:mm A")} —{" "}
                                        {b.endedAt ? dayjs(b.endedAt).format("hh:mm A") : "ongoing"}
                                    </span>
                                    <span style={{ fontWeight: 600, minWidth: 70, textAlign: "right", color: "#b45309" }}>
                                        {(() => {
                                            const sec = b.durationSec || 0;
                                            const h = Math.floor(sec / 3600);
                                            const m = Math.round((sec % 3600) / 60);
                                            return h > 0 ? `${h}h ${m}m` : `${m}m`;
                                        })()}
                                    </span>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* App Activity panel */}
            {showAppActivity && appSummary && (
                <AppActivityPanel summary={appSummary} date={selectedDate} />
            )}

            {/* Hour-grouped slots */}
            {day && (day.hours?.length || 0) === 0 && (
                <Box sx={{ p: 3, textAlign: "center", bgcolor: "#fff", borderRadius: 2, border: "1px solid #e1e5eb" }}>
                    <Typography color="text.secondary">No activity recorded on this date.</Typography>
                </Box>
            )}

            {day &&
                day.hours?.map((h) => (
                    <HourRow
                        key={h.hour}
                        hour={h}
                        notes={notes}
                        editable={editable}
                        selectedSlots={selectedSlots}
                        slotDrafts={slotDrafts}
                        commonSaving={commonSaving}
                        onToggleSlot={handleToggleSlot}
                        onToggleHour={handleToggleHour}
                        onAddNote={handleAddNoteClick}
                        onEditNote={handleEditNoteClick}
                        onUpdateSlotDraft={handleUpdateSlotDraft}
                        onSaveSingleSlot={handleSaveSingleSlot}
                        onOpenCarousel={(shots, index) => setCarouselShots({ shots, index })}
                    />
                ))}

            {/* Common Floating Note Bar for multi-selected slots */}
            {editable && selectedSlots.length > 0 && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "calc(100% - 40px)",
                        maxWidth: 1080,
                        zIndex: 1300,
                        bgcolor: "#ffffff",
                        borderRadius: 2.5,
                        boxShadow: "0 12px 36px rgba(15, 23, 42, 0.22), 0 2px 8px rgba(0, 128, 128, 0.15)",
                        border: "1px solid #ccfbf1",
                        borderLeft: "6px solid #008080",
                        p: { xs: 1.5, sm: 2 },
                        "@keyframes slideUpBar": {
                            from: { transform: "translate(-50%, 40px)", opacity: 0 },
                            to: { transform: "translate(-50%, 0)", opacity: 1 },
                        },
                        animation: "slideUpBar 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
                    }}
                >
                    <Stack spacing={1.5}>
                        {/* Top row: selection count badge, time range summary, quick selection buttons */}
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={1}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                <Chip
                                    label={`${selectedSlots.length} slot${selectedSlots.length > 1 ? "s" : ""} selected (${totalSelectedNoteMinutes} min)`}
                                    size="small"
                                    sx={{
                                        bgcolor: "#008080",
                                        color: "#fff",
                                        fontWeight: 700,
                                        fontSize: 12,
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#475569",
                                        fontSize: 11,
                                        fontWeight: 500,
                                        maxWidth: { xs: "100%", sm: 460 },
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                    title={sortedSelectedSlots.map(formatSlotRange).join(", ")}
                                >
                                    {sortedSelectedSlots.slice(0, 3).map(formatSlotRange).join(", ")}
                                    {sortedSelectedSlots.length > 3 && ` +${sortedSelectedSlots.length - 3} more`}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#64748b",
                                        fontSize: 11,
                                        fontStyle: "italic",
                                        display: { xs: "none", md: "inline" },
                                    }}
                                >
                                    (Edit separate notes on each card above, or quick-fill all below)
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleSelectAllDay}
                                    sx={{ fontSize: 11, color: "#008080", textTransform: "none", p: 0.5, fontWeight: 600 }}
                                >
                                    Select all day
                                </Button>
                                <Divider orientation="vertical" flexItem sx={{ height: 16, my: "auto" }} />
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleClearSelection}
                                    sx={{ fontSize: 11, color: "#64748b", textTransform: "none", p: 0.5 }}
                                >
                                    Clear selection
                                </Button>
                            </Stack>
                        </Stack>

                        {/* Bottom row: Quick apply section (left) + Common Save / Cancel (right) */}
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1.5}
                            alignItems={{ xs: "stretch", md: "center" }}
                            justifyContent="space-between"
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                sx={{
                                    flex: 1,
                                    bgcolor: "#f8fafc",
                                    p: 0.75,
                                    borderRadius: 1.5,
                                    border: "1px dashed #cbd5e1",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: "#475569",
                                        whiteSpace: "nowrap",
                                        mr: 0.5,
                                    }}
                                >
                                    Quick fill all:
                                </Typography>
                                <Select
                                    value={commonReason}
                                    onChange={(e) => setCommonReason(e.target.value)}
                                    size="small"
                                    sx={{
                                        minWidth: 120,
                                        bgcolor: "#ffffff",
                                        fontSize: 11,
                                        "& .MuiSelect-select": { py: 0.5 },
                                    }}
                                >
                                    {NOTE_REASONS.map((r) => (
                                        <MenuItem key={r.value} value={r.value} sx={{ fontSize: 11 }}>
                                            {r.label}
                                        </MenuItem>
                                    ))}
                                </Select>

                                <TextField
                                    size="small"
                                    placeholder="Common text to apply to all selected slots…"
                                    value={commonText}
                                    onChange={(e) => setCommonText(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        minWidth: 160,
                                        bgcolor: "#ffffff",
                                        "& .MuiInputBase-input": { fontSize: 11, py: 0.5, px: 1 },
                                    }}
                                />

                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleApplyCommonToAll}
                                    sx={{
                                        fontSize: 11,
                                        textTransform: "none",
                                        borderColor: "#008080",
                                        color: "#008080",
                                        whiteSpace: "nowrap",
                                        py: 0.5,
                                        "&:hover": { bgcolor: "rgba(0, 128, 128, 0.08)", borderColor: "#006666" },
                                    }}
                                >
                                    Apply to all
                                </Button>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    onClick={handleSaveCommonNotes}
                                    disabled={commonSaving}
                                    sx={{
                                        bgcolor: "#008080",
                                        "&:hover": { bgcolor: "#006666" },
                                        fontSize: 12,
                                        fontWeight: 700,
                                        textTransform: "none",
                                        px: 2.5,
                                        py: 0.9,
                                        borderRadius: 1.5,
                                        boxShadow: "0 2px 6px rgba(0, 128, 128, 0.3)",
                                        whiteSpace: "nowrap",
                                        minWidth: 140,
                                    }}
                                >
                                    {commonSaving ? (
                                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                                    ) : (
                                        `Save All Notes (${selectedSlots.length})`
                                    )}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleClearSelection}
                                    disabled={commonSaving}
                                    sx={{
                                        fontSize: 12,
                                        textTransform: "none",
                                        borderColor: "#cbd5e1",
                                        color: "#64748b",
                                        py: 0.9,
                                        px: 2,
                                        borderRadius: 1.5,
                                        "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>

                        <Typography sx={{ fontSize: 10, color: "#64748b", lineHeight: 1.2 }}>
                            ℹ️ Counts as work time only after a manager approves it. Each selected card can have its own separate reason and note.
                        </Typography>
                    </Stack>
                </Box>
            )}

            {/* Screenshot carousel */}
            <ScreenshotCarousel
                shots={carouselShots?.shots}
                index={carouselShots?.index ?? 0}
                onIndexChange={(i) => setCarouselShots((s) => (s ? { ...s, index: i } : s))}
                onClose={() => setCarouselShots(null)}
            />

            {/* Live screen view */}
            <LiveScreenModal
                open={showLive}
                onClose={() => setShowLive(false)}
                userId={userId}
                userName={userName}
            />
        </div>
    );
};

// ────────────────────────────────────────────────────────────────────────
// App Activity panel
// ────────────────────────────────────────────────────────────────────────
function AppActivityPanel({ summary, date }) {
    const topApps = summary.topApps || [];
    // Move "unknown" to the bottom so meaningful apps appear first.
    const sortedApps = useMemo(() => {
        return [...topApps].sort((a, b) => {
            const aU = a.app === "unknown";
            const bU = b.app === "unknown";
            if (aU && !bU) return 1;
            if (!aU && bU) return -1;
            return b.minutes - a.minutes;
        });
    }, [topApps]);

    const maxMin = Math.max(...sortedApps.map((a) => a.minutes), 1);
    const hasUnknown = sortedApps.some((a) => a.app === "unknown" && a.minutes >= 5);

    return (
        <Box
            sx={{
                border: "1px solid lightgray",
                padding: 2,
                marginBottom: 2,
                borderRadius: 2,
                bgcolor: "#fff",
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MonitorIcon fontSize="small" /> App Activity for {dayjs(date).format("ddd, MMM D YYYY")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {sortedApps.length} app(s) · {summary.sampleCount} samples
                </Typography>
            </Stack>

            {/* Productivity now lives on its own page — opened via the "Productivity"
                button in the user's day header (see ProductivityPage). */}

            {/* Top Websites — browser activity grouped by domain (chatgpt.com,
                localhost:5173, …). Captured via the desktop URL reader. */}
            {summary.topSites && summary.topSites.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                        <LanguageIcon fontSize="small" /> Top Websites
                    </Typography>
                    <Stack spacing={0.75}>
                        {(() => {
                            const maxSiteMin = Math.max(...summary.topSites.map((s) => s.minutes), 1);
                            return summary.topSites.map((s) => (
                                <Box
                                    key={s.domain}
                                    sx={{
                                        bgcolor: "#f0f9ff",
                                        p: 1,
                                        borderRadius: 1,
                                        border: "1px solid #bae6fd",
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={0.5}
                                    >
                                        <Tooltip title={s.url || s.domain} placement="top" arrow>
                                            <strong style={{ fontSize: 13, cursor: "help" }}>{s.domain}</strong>
                                        </Tooltip>
                                        <strong style={{ color: "#0369a1", fontSize: 12 }}>
                                            {Math.floor(s.minutes / 60)}h {s.minutes % 60}m
                                        </strong>
                                    </Stack>
                                    <Box sx={{ height: 5, bgcolor: "#e0f2fe", borderRadius: 1 }}>
                                        <Box
                                            sx={{
                                                width: `${(s.minutes / maxSiteMin) * 100}%`,
                                                height: "100%",
                                                bgcolor: "#0ea5e9",
                                                borderRadius: 1,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ));
                        })()}
                    </Stack>
                </Box>
            )}

            {sortedApps.length === 0 ? (
                <Typography color="text.secondary">No app data recorded on this date.</Typography>
            ) : (
                <Stack spacing={1.25}>
                    {hasUnknown && (
                        <Box
                            sx={{
                                bgcolor: "#fef3c7",
                                border: "1px solid #d97706",
                                p: 1.25,
                                borderRadius: 1,
                                fontSize: 12,
                                color: "#92400e",
                            }}
                        >
                            <strong>Tip:</strong> Many minutes are showing as "unknown" — this means macOS
                            Accessibility permission isn't granted to the desktop app, so it can't read the
                            foreground app name.
                        </Box>
                    )}
                    {sortedApps.map((a) => {
                        const isUnknown = a.app === "unknown";
                        return (
                            <Box
                                key={a.app}
                                sx={{
                                    bgcolor: isUnknown ? "#f1f5f9" : "#f8fafc",
                                    p: 1.25,
                                    borderRadius: 1,
                                    border: "1px solid #e1e5eb",
                                    opacity: isUnknown ? 0.7 : 1,
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                                    <Box>
                                        <strong style={{ fontSize: 14 }}>{a.app}</strong>
                                        {isUnknown && (
                                            <span
                                                style={{
                                                    marginLeft: 6,
                                                    fontSize: 11,
                                                    color: "#94a3b8",
                                                }}
                                            >
                                                (app not detected)
                                            </span>
                                        )}
                                    </Box>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        sx={{ fontSize: 12, color: "#6b7280" }}
                                    >
                                        <span>
                                            <KeyboardIcon
                                                sx={{ fontSize: 13, verticalAlign: "middle", mr: 0.5 }}
                                            />
                                            {a.keys.toLocaleString()}
                                        </span>
                                        <span>
                                            <MouseIcon
                                                sx={{ fontSize: 13, verticalAlign: "middle", mr: 0.5 }}
                                            />
                                            {a.mouse.toLocaleString()}
                                        </span>
                                        <span style={{ color: "#4c8dff", fontWeight: 600 }}>
                                            {a.avgActivity}% active
                                        </span>
                                        <strong style={{ color: "#1f2937" }}>
                                            {Math.floor(a.minutes / 60)}h {a.minutes % 60}m
                                        </strong>
                                    </Stack>
                                </Stack>
                                <Box sx={{ height: 6, bgcolor: "#eef2f7", borderRadius: 1 }}>
                                    <Box
                                        sx={{
                                            width: `${(a.minutes / maxMin) * 100}%`,
                                            height: "100%",
                                            bgcolor: isUnknown ? "#94a3b8" : "#4c8dff",
                                            borderRadius: 1,
                                        }}
                                    />
                                </Box>
                                {a.sampleTitles && a.sampleTitles.length > 0 && (
                                    <Stack
                                        spacing={0.25}
                                        sx={{
                                            mt: 0.75,
                                            pl: 0.5,
                                            maxHeight: 220,
                                            overflowY: "auto",
                                        }}
                                    >
                                        {a.sampleTitles.map((t, idx) => {
                                            // Backwards-compat: tolerate the old shape where sampleTitles was string[].
                                            const rawTitle = typeof t === "string" ? t : t.title;
                                            const titleMins = typeof t === "string" ? null : t.minutes;
                                            // OS window titles arrive as "<page> - <app>"; show as "<app> - <page>"
                                            // so the app the user already sees in the row header reads naturally.
                                            const appSuffix = ` - ${a.app}`;
                                            const pagePart = rawTitle.endsWith(appSuffix)
                                                ? rawTitle.slice(0, -appSuffix.length)
                                                : rawTitle;
                                            const titleText = pagePart && pagePart !== a.app
                                                ? `${a.app} - ${pagePart}`
                                                : a.app;
                                            return (
                                                <Tooltip
                                                    key={idx}
                                                    title={titleText}
                                                    placement="top"
                                                    followCursor
                                                    enterDelay={0}
                                                    enterNextDelay={0}
                                                    leaveDelay={0}
                                                    arrow
                                                >
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        spacing={1}
                                                        sx={{ cursor: "help", maxWidth: "100%" }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: "#64748b",
                                                                fontSize: 11,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                flex: 1,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            · {titleText}
                                                        </Typography>
                                                        {titleMins != null && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: "#94a3b8",
                                                                    fontSize: 11,
                                                                    whiteSpace: "nowrap",
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {titleMins < 60
                                                                    ? `${titleMins}m`
                                                                    : `${Math.floor(titleMins / 60)}h ${titleMins % 60}m`}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </Tooltip>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Hour row — header + 6-slot grid
// ────────────────────────────────────────────────────────────────────────
function HourRow({
    hour,
    notes,
    editable,
    selectedSlots = [],
    slotDrafts = {},
    commonSaving = false,
    onToggleSlot,
    onToggleHour,
    onAddNote,
    onEditNote,
    onUpdateSlotDraft,
    onSaveSingleSlot,
    onOpenCarousel,
}) {
    const totalMinutes = hour.workMinutes || 0;
    const hourSlotKeys = (hour.slots || [])
        .filter(isSlotEligibleForNote)
        .map((s) => `${hour.hour}:${s.slotIdx}`);
    const hasSelectableSlots = hourSlotKeys.length > 0;
    const isHourFullySelected = hasSelectableSlots && hourSlotKeys.every((k) => selectedSlots.includes(k));
    const isHourPartiallySelected = !isHourFullySelected && hourSlotKeys.some((k) => selectedSlots.includes(k));

    return (
        <div className="hour-row">
            <div
                className="hour-header"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                }}
            >
                <div>
                    {fmtTime(hour.hour, 0)} - {fmtTime((hour.hour + 1) % 24, 0)}
                    <span className="active-minutes">
                        Total Time Worked :{" "}
                        {totalMinutes < 60
                            ? `${totalMinutes} min`
                            : `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`}
                    </span>
                </div>
                {editable && hasSelectableSlots && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => onToggleHour(hour.hour)}
                        startIcon={
                            <Checkbox
                                size="small"
                                checked={isHourFullySelected}
                                indeterminate={isHourPartiallySelected}
                                sx={{
                                    p: 0,
                                    color: "#008080",
                                    "&.Mui-checked": { color: "#008080" },
                                    "&.MuiCheckbox-indeterminate": { color: "#008080" },
                                }}
                            />
                        }
                        sx={{
                            fontSize: 11,
                            color: "#008080",
                            textTransform: "none",
                            fontWeight: 600,
                            py: 0.25,
                            px: 1,
                            borderRadius: 1,
                            "&:hover": { bgcolor: "rgba(0, 128, 128, 0.08)" },
                        }}
                    >
                        {isHourFullySelected ? "Deselect hour" : "Select hour"}
                    </Button>
                )}
            </div>
            <div className="hour-grid">
                {hour.slots.map((slot) => {
                    const slotKey = `${hour.hour}:${slot.slotIdx}`;
                    return (
                        <SlotCard
                            key={slot.slotIdx}
                            slot={slot}
                            hour={hour.hour}
                            slotKey={slotKey}
                            note={notes[slotKey]}
                            editable={editable}
                            isSelected={selectedSlots.includes(slotKey)}
                            selectedCount={selectedSlots.length}
                            draft={slotDrafts[slotKey]}
                            isSaving={commonSaving}
                            onUpdateDraft={onUpdateSlotDraft}
                            onSaveSingle={onSaveSingleSlot}
                            onToggleSelect={() => onToggleSlot(slotKey)}
                            onAddNote={() => onAddNote(slotKey)}
                            onEditNote={() => onEditNote(slotKey)}
                            onOpenCarousel={onOpenCarousel}
                        />
                    );
                })}
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Slot card — one 10-minute cell
// ────────────────────────────────────────────────────────────────────────
function SlotCard({
    slot,
    hour,
    slotKey,
    note,
    editable,
    isSelected = false,
    selectedCount = 0,
    draft,
    isSaving = false,
    onUpdateDraft,
    onSaveSingle,
    onToggleSelect,
    onAddNote,
    onEditNote,
    onOpenCarousel,
}) {
    const {
        activityPct,
        screenshots = [],
        sampleCount,
        workSec,
        hasData,
        slotIdx,
        totalKeys,
        totalMouse,
        apps,
        onBreak,
        suspiciousScore = 0,
        suspiciousReasons = [],
        suspiciousAppName = "",
        keyboardActiveSeconds = 0,
        mouseActiveSeconds = 0,
        // Minutes an approved note added to this slot (10 minus what was
        // already tracked), so approving never double-counts real activity.
        approvedNoteMinutes = 0,
        pendingNoteMinutes = 0,
    } = slot;

    const noteStatus = noteStatusOf(note);
    const noteStyle = noteStatus ? NOTE_STATUS_STYLES[noteStatus] : null;

    const screenshot = screenshots[0] || null;
    const hasScreenshot = Boolean(screenshot);

    // Colored border ONLY when a concrete jiggler / automation app was detected
    // (suspiciousAppName is set). Old records came from the now-removed pattern
    // analyzer and only carry a pattern-derived score with no app name — we
    // leave them uncoloured so legacy slots don't appear flagged anymore.
    //   App detected → red (60+) or blue (30-59)
    //   No app name  → no highlight (even if score > 0)
    const suspiciousColor = suspiciousAppName
        ? (suspiciousScore >= 60 ? "#ef4444" : "#3b82f6")
        : null;
    const [hovered, setHovered] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const cardRef = React.useRef(null);

    const startLabel = fmtTime(hour, slotIdx * 10);
    const endLabel = fmtTime(
        slotIdx === 5 ? (hour + 1) % 24 : hour,
        slotIdx === 5 ? 0 : (slotIdx + 1) * 10
    );
    const minutes = Math.round(workSec / 60);
    const barColor = getProgressColor(activityPct);
    const totalEvents = (totalKeys || 0) + (totalMouse || 0);
    // Hubstaff-style seconds-based breakdown.
    //   keyboardPct = seconds_with_keyboard_input / window_seconds
    //   mousePct    = seconds_with_mouse_input    / window_seconds
    // These two don't sum to activityPct because a single second can have both
    // inputs and contribute to both. Matches how Hubstaff shows e.g.
    // "Mouse 68% / Keyboard 13%" with Overall 75%. Falls back to the legacy
    // event-count split when the new fields are missing (older rows).
    const slotWindowSec = Math.max(1, (sampleCount || 0) * 60);
    const hasSecondsBreakdown = (keyboardActiveSeconds || mouseActiveSeconds) > 0;
    const keyboardPct = hasSecondsBreakdown
        ? Math.round((keyboardActiveSeconds / slotWindowSec) * 100)
        : (totalEvents > 0 ? Math.round(activityPct * ((totalKeys || 0) / totalEvents)) : 0);
    const mousePct = hasSecondsBreakdown
        ? Math.round((mouseActiveSeconds / slotWindowSec) * 100)
        : (totalEvents > 0 ? Math.round(activityPct * ((totalMouse || 0) / totalEvents)) : 0);

    function handleMouseEnter() {
        if (cardRef.current) {
            const rect = cardRef.current.getBoundingClientRect();
            const TOPBAR = 58;
            const TIP_H = 290;
            const TIP_W = 220;
            const spaceBelow = window.innerHeight - rect.bottom;
            const top =
                spaceBelow >= TIP_H ? rect.bottom + 6 : Math.max(TOPBAR + 4, rect.top - TIP_H - 6);
            const left = Math.min(
                window.innerWidth - TIP_W - 8,
                Math.max(8, rect.left + rect.width / 2 - TIP_W / 2)
            );
            setTooltipPos({ top, left });
        }
        setHovered(true);
    }

    // Thumbnail / placeholder
    let thumb;
    if (screenshot && screenshot.data) {
        thumb = (
            <button
                onClick={() => onOpenCarousel(screenshots, 0)}
                style={{
                    padding: 0,
                    background: "#eef2f7",
                    border: 0,
                    borderRadius: 4,
                    cursor: "pointer",
                    overflow: "hidden",
                    aspectRatio: "16/10",
                    position: "relative",
                }}
            >
                <img
                    src={screenshot.data}
                    alt="screenshot"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                {screenshots.length > 1 && (
                    <span
                        style={{
                            position: "absolute",
                            bottom: 3,
                            right: 3,
                            background: "rgba(0,0,0,0.65)",
                            color: "#fff",
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "1px 5px",
                            borderRadius: 10,
                        }}
                    >
                        {screenshots.length}
                    </span>
                )}
            </button>
        );
    } else if (screenshot) {
        thumb = (
            <button
                onClick={() => onOpenCarousel(screenshots, 0)}
                style={{
                    padding: 0,
                    background: "#eef2f7",
                    border: 0,
                    borderRadius: 4,
                    cursor: "pointer",
                    aspectRatio: "16/10",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    fontSize: 11,
                }}
            >
                View
            </button>
        );
    } else if (hasData) {
        thumb = (
            <div
                style={{
                    background: "#eef2f7",
                    borderRadius: 4,
                    aspectRatio: "16/10",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    fontSize: 10,
                }}
            >
                <BrokenImageIcon sx={{ fontSize: 16, mr: 0.5 }} /> IMAGE NOT AVAILABLE
            </div>
        );
    } else if (onBreak) {
        thumb = (
            <div
                style={{
                    background: "#fef3c7",
                    borderRadius: 4,
                    aspectRatio: "16/10",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#92400e",
                    fontSize: 11,
                    fontWeight: 600,
                    gap: 4,
                }}
            >
                <LocalCafeIcon sx={{ fontSize: 18 }} />
                On Break
            </div>
        );
    } else {
        thumb = (
            <Box
                sx={{
                    bgcolor: "#f1f5f9",
                    borderRadius: 1,
                    aspectRatio: "16/10",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    fontSize: 11,
                    fontWeight: 600,
                }}
            >
                No Activity
            </Box>
        );
    }

    return (
        <div
            ref={cardRef}
            className="hour-cell"
            style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                background: isSelected ? "#f0fdfa" : "#f8fafc",
                // Suspicion border takes priority over the green "has note" border:
                // a flagged slot is more important to highlight than a noted one.
                border: isSelected
                    ? "2px solid #008080"
                    : suspiciousColor
                        ? `2px solid ${suspiciousColor}`
                        : noteStyle
                            ? `2px solid ${noteStyle.border}`
                            : "1px solid #e1e5eb",
                boxShadow: isSelected
                    ? "0 0 0 2px rgba(0, 128, 128, 0.25), 0 2px 8px rgba(0, 0, 0, 0.08)"
                    : suspiciousColor
                        ? `0 0 0 2px ${suspiciousColor}33`
                        : "none",
                minWidth: 0,
                transition: "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
            }}
        >
            {/* Multi-select Checkbox - shown on No Activity slots, slots below 10 min, and screenshot slots with activity <= 10% */}
            {editable && isSlotEligibleForNote(slot) && (
                <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={(e) => {
                        e.stopPropagation();
                        onToggleSelect();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: "absolute",
                        top: 4,
                        left: 4,
                        zIndex: 4,
                        p: "2px",
                        color: isSelected ? "#008080" : "#64748b",
                        "&.Mui-checked": {
                            color: "#008080",
                        },
                        backgroundColor: isSelected ? "rgba(240, 253, 250, 0.95)" : "rgba(255, 255, 255, 0.9)",
                        borderRadius: "4px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
                        "&:hover": {
                            backgroundColor: "#ffffff",
                        },
                    }}
                />
            )}

            {/* Suspicious-pattern badge — visible without opening the slot */}
            {suspiciousColor && (
                <div
                    title={
                        suspiciousAppName
                            ? `Detected: ${suspiciousAppName}${suspiciousReasons.length ? ` (${suspiciousReasons.join(", ")})` : ""}`
                            : suspiciousReasons.length
                                ? `Suspicious pattern: ${suspiciousReasons.join(", ")}`
                                : "Suspicious activity pattern detected"
                    }
                    style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        background: suspiciousColor,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 10,
                        zIndex: 2,
                        pointerEvents: "auto",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        maxWidth: 140,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    ⚠ {suspiciousAppName || `${suspiciousScore}%`}
                </div>
            )}
            {/* Only the image area triggers the activity tooltip */}
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setHovered(false)}
            >
                {thumb}
            </div>

            <div
                style={{
                    marginTop: 6,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <span style={{ fontSize: 11, fontWeight: 600 }}>
                    {startLabel} - {endLabel}
                </span>
                {editable && (
                    <Tooltip title={note ? "Edit note for this slot" : "Add note"}>
                        <EditIcon
                            sx={{
                                fontSize: 15,
                                cursor: "pointer",
                                color: noteStyle ? noteStyle.border : "#008080",
                                "&:hover": { transform: "scale(1.15)" },
                                transition: "transform 0.15s",
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditNote();
                            }}
                        />
                    </Tooltip>
                )}
            </div>

            <LinearProgress
                variant="determinate"
                value={Math.min(100, activityPct)}
                sx={{
                    mt: 0.5,
                    borderRadius: 1,
                    height: 5,
                    bgcolor: "#eef2f7",
                    "& .MuiLinearProgress-bar": { backgroundColor: barColor },
                }}
            />

            <Typography variant="caption" sx={{ mt: 0.5, color: "#6b7280" }}>
                {hasData || approvedNoteMinutes > 0
                    ? `${activityPct}% of ${Math.max(minutes, sampleCount)} min`
                    : onBreak
                        ? "Break"
                        : "—"}
                {approvedNoteMinutes > 0 && (
                    <span style={{ color: "#15803d", fontWeight: 600 }}>
                        {" "}
                        (+{approvedNoteMinutes} min approved)
                    </span>
                )}
                {pendingNoteMinutes > 0 && (
                    <span style={{ color: "#b45309", fontWeight: 600 }}>
                        {" "}
                        (+{pendingNoteMinutes} min pending)
                    </span>
                )}
            </Typography>

            {/* Note row */}
            {editable && isSelected ? (
                <Box
                    sx={{
                        mt: 0.75,
                        borderTop: "1px dashed #008080",
                        pt: 0.75,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Stack spacing={0.6}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#008080" }}>
                                Separate Note
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleSelect();
                                }}
                                sx={{
                                    fontSize: 9,
                                    color: "#94a3b8",
                                    p: 0,
                                    minWidth: "auto",
                                    textTransform: "none",
                                    "&:hover": { color: "#ef4444" },
                                }}
                            >
                                Deselect
                            </Button>
                        </Box>

                        <Select
                            value={draft?.reason || "meeting"}
                            onChange={(e) => {
                                e.stopPropagation();
                                onUpdateDraft(slotKey, "reason", e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                            sx={{
                                fontSize: 11,
                                bgcolor: "#ffffff",
                                borderRadius: 1,
                                "& .MuiSelect-select": { py: 0.4, px: 0.8 },
                            }}
                        >
                            {NOTE_REASONS.map((r) => (
                                <MenuItem key={r.value} value={r.value} sx={{ fontSize: 11 }}>
                                    {r.label}
                                </MenuItem>
                            ))}
                        </Select>

                        <TextField
                            size="small"
                            placeholder="Note for this slot…"
                            value={draft?.text !== undefined ? draft.text : ""}
                            onChange={(e) => {
                                e.stopPropagation();
                                onUpdateDraft(slotKey, "text", e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (selectedCount === 1) {
                                        onSaveSingle(slotKey);
                                    }
                                }
                            }}
                            inputProps={{ style: { fontSize: 11, padding: "4px 6px" } }}
                            sx={{ bgcolor: "#ffffff", borderRadius: 1 }}
                        />

                        {selectedCount === 1 ? (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSaveSingle(slotKey);
                                    }}
                                    disabled={isSaving}
                                    sx={{
                                        fontSize: 10,
                                        py: 0.25,
                                        px: 1,
                                        bgcolor: "#008080",
                                        "&:hover": { bgcolor: "#006666" },
                                        textTransform: "none",
                                    }}
                                >
                                    {isSaving ? "…" : "Save"}
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleSelect();
                                    }}
                                    disabled={isSaving}
                                    sx={{
                                        fontSize: 10,
                                        py: 0.25,
                                        px: 1,
                                        borderColor: "#cbd5e1",
                                        color: "#64748b",
                                        textTransform: "none",
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Stack>
                        ) : (
                            <Typography sx={{ fontSize: 9, color: "#008080", fontWeight: 600, mt: 0.25 }}>
                                ✓ Ready for common Save
                            </Typography>
                        )}
                    </Stack>
                </Box>
            ) : (
                <Box sx={{ mt: 0.75, borderTop: "1px dashed #e1e5eb", pt: 0.75 }}>
                    {note ? (
                        <Box sx={{ fontSize: 10, minWidth: 0 }}>
                            <Tooltip
                                title={
                                    (NOTE_REASONS.find((r) => r.value === note.reason)?.label || note.reason) +
                                    (note.text ? ` — ${note.text}` : "")
                                }
                                placement="top"
                                followCursor
                                enterDelay={0}
                                enterNextDelay={0}
                                leaveDelay={0}
                                arrow
                            >
                                <Box
                                    sx={{
                                        fontSize: 10,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                        cursor: "help",
                                    }}
                                >
                                    <strong style={{ color: "#1f2937" }}>
                                        {NOTE_REASONS.find((r) => r.value === note.reason)?.label || note.reason}
                                    </strong>
                                    {note.text ? <span style={{ color: "#6b7280" }}> — {note.text}</span> : null}
                                </Box>
                            </Tooltip>
                            <Tooltip
                                title={
                                    noteStatus === "approved"
                                        ? `Approved${note.reviewedByName ? ` by ${note.reviewedByName}` : ""} — ${approvedNoteMinutes || note.creditedMinutes || 0} min added to work time`
                                        : noteStatus === "rejected"
                                            ? `Rejected${note.reviewedByName ? ` by ${note.reviewedByName}` : ""}${note.reviewNote ? ` — ${note.reviewNote}` : ""} — no time added`
                                            : `Waiting for a manager to approve — ${pendingNoteMinutes || Math.max(0, 10 - (slot.trackedMinutes || 0))} min pending`
                                }
                                placement="top"
                                arrow
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "inline-block",
                                        mt: 0.25,
                                        px: 0.6,
                                        borderRadius: 5,
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: noteStyle.color,
                                        border: `1px solid ${noteStyle.border}`,
                                        cursor: "help",
                                    }}
                                >
                                    {noteStyle.label}
                                </Typography>
                            </Tooltip>
                            {note.history?.length > 0 && (
                                <Tooltip
                                    title={`Edited ${note.history.length} time(s)`}
                                    placement="top"
                                    enterDelay={0}
                                    enterNextDelay={0}
                                    leaveDelay={0}
                                    arrow
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#94a3b8", fontSize: 9, mt: 0.25, ml: 0.5, cursor: "help" }}
                                    >
                                        (edited)
                                    </Typography>
                                </Tooltip>
                            )}
                        </Box>
                    ) : editable ? (
                        <Button
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddNote();
                            }}
                            sx={{
                                fontSize: 10,
                                p: 0,
                                color: "#008080",
                                fontWeight: 500,
                                textTransform: "none",
                            }}
                        >
                            + Add note
                        </Button>
                    ) : null}
                </Box>
            )}

            {/* Hover tooltip — keyboard/mouse breakdown + apps used */}
            {hovered && hasData && (
                <div
                    style={{
                        position: "fixed",
                        top: tooltipPos.top,
                        left: tooltipPos.left,
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: 8,
                        padding: 10,
                        width: 220,
                        zIndex: 9999,
                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
                        pointerEvents: "none",
                    }}
                >
                    <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 2, color: "#1f2937" }}>
                        {startLabel} – {endLabel}
                    </div>
                    <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 8 }}>
                        Activity {activityPct}% = keyboard + mouse
                    </div>

                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                            <KeyboardIcon sx={{ fontSize: 13, color: "#4c8dff" }} /> Keyboard
                        </span>
                        <strong style={{ fontSize: 12, color: "#4c8dff" }}>{keyboardPct}%</strong>
                    </Stack>
                    <div style={{ height: 4, background: "#f8fafc", borderRadius: 2, marginBottom: 8 }}>
                        <div style={{ width: `${keyboardPct}%`, height: "100%", background: "#4c8dff", borderRadius: 2 }} />
                    </div>

                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                            <MouseIcon sx={{ fontSize: 13, color: "#e0a23a" }} /> Mouse
                        </span>
                        <strong style={{ fontSize: 12, color: "#e0a23a" }}>{mousePct}%</strong>
                    </Stack>
                    <div style={{ height: 4, background: "#f8fafc", borderRadius: 2, marginBottom: 8 }}>
                        <div style={{ width: `${mousePct}%`, height: "100%", background: "#e0a23a", borderRadius: 2 }} />
                    </div>

                    <div style={{ borderTop: "1px solid #e1e5eb", paddingTop: 6, fontSize: 10, color: "#6b7280" }}>
                        <div>Keystrokes: <strong style={{ color: "#1f2937" }}>{(totalKeys || 0).toLocaleString()}</strong></div>
                        <div>Mouse events: <strong style={{ color: "#1f2937" }}>{(totalMouse || 0).toLocaleString()}</strong></div>
                        <div>Activity rate: <strong style={{ color: "#1f2937" }}>{activityPct}%</strong></div>
                    </div>

                    {apps && apps.length > 0 && (
                        <div style={{ borderTop: "1px solid #e1e5eb", paddingTop: 6, marginTop: 6 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#6b7280", marginBottom: 4 }}>
                                <MonitorIcon sx={{ fontSize: 12 }} /> Apps used
                            </div>
                            {apps.map((a) => (
                                <div key={a.app} style={{ marginBottom: 4 }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 10 }}>
                                        <span style={{ color: "#1f2937", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }}>
                                            {a.app}
                                        </span>
                                        <span style={{ color: "#6b7280" }}>{a.minutes}m</span>
                                    </Stack>
                                    {a.sampleTitles && a.sampleTitles[0] && (
                                        <div style={{ fontSize: 9, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
                                            {a.sampleTitles[0]}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Screenshot carousel — modal with arrows + thumbnail strip
// ────────────────────────────────────────────────────────────────────────
function ScreenshotCarousel({ shots, index, onIndexChange, onClose }) {
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const dragRef = React.useRef(null);
    const [dragging, setDragging] = useState(false);

    const MIN_SCALE = 0.5;
    const MAX_SCALE = 5;
    const STEP = 0.25;

    const resetZoom = React.useCallback(() => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
    }, []);
    const zoomIn = React.useCallback(
        () => setScale((s) => Math.min(MAX_SCALE, +(s + STEP).toFixed(2))),
        []
    );
    const zoomOut = React.useCallback(
        () => setScale((s) => Math.max(MIN_SCALE, +(s - STEP).toFixed(2))),
        []
    );

    // Reset zoom + pan whenever the image changes.
    React.useEffect(() => {
        resetZoom();
    }, [index, shots, resetZoom]);

    React.useEffect(() => {
        if (!shots) return;
        function onKey(e) {
            if (e.key === "ArrowLeft" && index > 0) onIndexChange(index - 1);
            if (e.key === "ArrowRight" && index < shots.length - 1) onIndexChange(index + 1);
            if (e.key === "Escape") onClose();
            if (e.key === "+" || e.key === "=") zoomIn();
            if (e.key === "-" || e.key === "_") zoomOut();
            if (e.key === "0") resetZoom();
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [shots, index, onIndexChange, onClose, zoomIn, zoomOut, resetZoom]);

    // Drag-to-pan when zoomed in.
    function onMouseDown(e) {
        if (scale <= 1) return;
        e.preventDefault();
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            baseX: offset.x,
            baseY: offset.y,
        };
        setDragging(true);
    }
    function onMouseMove(e) {
        if (!dragRef.current) return;
        setOffset({
            x: dragRef.current.baseX + (e.clientX - dragRef.current.startX),
            y: dragRef.current.baseY + (e.clientY - dragRef.current.startY),
        });
    }
    function onMouseUp() {
        dragRef.current = null;
        setDragging(false);
    }
    function onWheel(e) {
        // Trackpad/wheel zoom — scroll up = in, down = out.
        if (e.deltaY < 0) zoomIn();
        else zoomOut();
    }

    if (!shots || shots.length === 0) return null;
    const shot = shots[index] || shots[0];
    const total = shots.length;
    const cursorWhenZoomed = dragging ? "grabbing" : "grab";

    return (
        <Dialog fullWidth open={!!shots} onClose={onClose} maxWidth="md">
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {new Date(shot.takenAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    {shot.width ? ` · ${shot.width}×${shot.height}` : ""} — {index + 1} / {total}
                </Typography>
                <Stack direction="row" spacing={0.25} alignItems="center">
                    <IconButton onClick={zoomOut} size="small" disabled={scale <= MIN_SCALE} title="Zoom out (-)">
                        <ZoomOutIcon fontSize="small" />
                    </IconButton>
                    <Typography
                        variant="caption"
                        sx={{
                            minWidth: 42,
                            textAlign: "center",
                            fontVariantNumeric: "tabular-nums",
                            color: "text.secondary",
                        }}
                    >
                        {Math.round(scale * 100)}%
                    </Typography>
                    <IconButton onClick={zoomIn} size="small" disabled={scale >= MAX_SCALE} title="Zoom in (+)">
                        <ZoomInIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={resetZoom} size="small" disabled={scale === 1 && offset.x === 0 && offset.y === 0} title="Reset zoom (0)">
                        <RestartAltIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={onClose} size="small" title="Close (Esc)">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent
                sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#0f172a",
                    py: 2,
                    overflow: "hidden",
                }}
            >
                <Box
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    onWheel={onWheel}
                    sx={{
                        width: "100%",
                        height: "65vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        cursor: scale > 1 ? cursorWhenZoomed : "default",
                        userSelect: "none",
                    }}
                >
                    {shot.fullData || shot.data ? (
                        <img
                            src={shot.fullData || shot.data}
                            alt={`Screenshot ${index + 1}`}
                            draggable={false}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                borderRadius: 8,
                                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                                transformOrigin: "center center",
                                transition: dragging ? "none" : "transform 0.12s ease-out",
                                willChange: "transform",
                                pointerEvents: "none",
                            }}
                        />
                    ) : (
                        <img src={NoImage} alt="No screenshot" style={{ maxWidth: "100%" }} />
                    )}
                </Box>

                {/* Foreground app / page at capture time — shown BELOW the screenshot. */}
                {(shot.activeApp || shot.activeTitle) && (
                    <Box
                        sx={{
                            mt: 1.5,
                            px: 1.5,
                            py: 0.75,
                            maxWidth: "100%",
                            bgcolor: "rgba(255,255,255,0.08)",
                            borderRadius: 1,
                            textAlign: "center",
                        }}
                        title={[shot.activeApp, shot.activeTitle].filter(Boolean).join(" — ")}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: "#fff", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                            {shot.activeApp}{shot.activeTitle ? `: ${shot.activeTitle}` : ""}
                        </Typography>
                        {shot.activeUrl && (
                            <Typography
                                variant="caption"
                                sx={{ color: "rgba(255,255,255,0.65)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                                {shot.activeUrl}
                            </Typography>
                        )}
                    </Box>
                )}

                {total > 1 && (
                    <>
                        <IconButton
                            onClick={() => onIndexChange(Math.max(0, index - 1))}
                            disabled={index === 0}
                            sx={{
                                position: "absolute",
                                left: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                bgcolor: "rgba(255,255,255,0.14)",
                                color: "#fff",
                                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                            }}
                        >
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            onClick={() => onIndexChange(Math.min(total - 1, index + 1))}
                            disabled={index === total - 1}
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                bgcolor: "rgba(255,255,255,0.14)",
                                color: "#fff",
                                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                            }}
                        >
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>

                        {/* Thumbnail strip */}
                        <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{
                                mt: 1.5,
                                p: 1,
                                bgcolor: "rgba(0,0,0,0.4)",
                                borderRadius: 1,
                                maxWidth: "100%",
                                overflowX: "auto",
                            }}
                        >
                            {shots.map((s, i) => (
                                <button
                                    key={s._id || i}
                                    onClick={() => onIndexChange(i)}
                                    style={{
                                        padding: 0,
                                        border: `2px solid ${i === index ? "#4c8dff" : "transparent"}`,
                                        borderRadius: 5,
                                        cursor: "pointer",
                                        background: "rgba(255,255,255,0.08)",
                                        width: 56,
                                        height: 36,
                                        overflow: "hidden",
                                        flexShrink: 0,
                                    }}
                                >
                                    {s.data ? (
                                        <img
                                            src={s.data}
                                            alt={`${i + 1}`}
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#64748b",
                                                fontSize: 9,
                                            }}
                                        >
                                            {i + 1}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </Stack>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default UserDetailView;
